(() => {
  'use strict';

  if (window.SparpostenProductGalleryV20) return;
  window.SparpostenProductGalleryV20 = true;

  const gallerySelector = '[data-product-gallery]';
  const thumbSelector = '[data-product-gallery-thumb]';
  const mainSelector = '[data-product-gallery-open]';
  const lightboxSelector = '[data-product-lightbox]';
  let openGallery = null;
  let previousBodyOverflow = '';
  let touchStartX = null;

  const asElement = (target) => target instanceof Element ? target : null;

  const getMainButton = (gallery) => gallery?.querySelector(mainSelector) || null;

  const getMainImage = (gallery) => gallery?.querySelector(
    '[data-product-gallery-main-image], .product-gallery__main-image, [data-product-gallery-open] img'
  ) || null;

  const getThumbnails = (gallery) => Array.from(gallery?.querySelectorAll(thumbSelector) || []);

  const normaliseIndex = (index, length) => {
    if (length < 1) return 0;
    return ((index % length) + length) % length;
  };

  const itemFromElement = (element, fallbackImage = null) => ({
    image: element?.dataset.image || fallbackImage?.currentSrc || fallbackImage?.src || '',
    fullImage: element?.dataset.fullImage || element?.dataset.image || fallbackImage?.currentSrc || fallbackImage?.src || '',
    alt: element?.dataset.imageAlt || fallbackImage?.alt || '',
  });

  const getItems = (gallery) => {
    const thumbnails = getThumbnails(gallery);
    if (thumbnails.length) return thumbnails.map((thumbnail) => itemFromElement(thumbnail));

    const mainButton = getMainButton(gallery);
    const mainImage = getMainImage(gallery);
    return [itemFromElement(mainButton, mainImage)];
  };

  const currentIndex = (gallery, length) => {
    const mainButton = getMainButton(gallery);
    const raw = Number.parseInt(mainButton?.dataset.galleryIndex || '0', 10);
    return normaliseIndex(Number.isFinite(raw) ? raw : 0, length);
  };

  const renderLightbox = (gallery, index) => {
    const lightbox = gallery?.querySelector(lightboxSelector);
    const lightboxImage = lightbox?.querySelector('[data-lightbox-image]');
    const lightboxCounter = lightbox?.querySelector('[data-lightbox-counter]');
    const items = getItems(gallery);
    if (!lightbox || !lightboxImage || !items.length) return;

    const safeIndex = normaliseIndex(index, items.length);
    const selected = items[safeIndex];
    lightboxImage.src = selected.fullImage || selected.image;
    lightboxImage.alt = selected.alt || '';
    if (lightboxCounter) lightboxCounter.textContent = `${safeIndex + 1} / ${items.length}`;
  };

  const selectImage = (gallery, index, updateLightbox = false) => {
    const mainButton = getMainButton(gallery);
    const mainImage = getMainImage(gallery);
    const thumbnails = getThumbnails(gallery);
    const items = getItems(gallery);
    if (!mainButton || !mainImage || !items.length) return false;

    const safeIndex = normaliseIndex(index, items.length);
    const selected = items[safeIndex];
    if (!selected.image) return false;

    mainImage.removeAttribute('srcset');
    mainImage.removeAttribute('sizes');
    mainImage.src = selected.image;
    mainImage.alt = selected.alt || '';

    mainButton.dataset.galleryIndex = String(safeIndex);
    mainButton.dataset.image = selected.image;
    mainButton.dataset.fullImage = selected.fullImage || selected.image;
    mainButton.dataset.imageAlt = selected.alt || '';

    thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const active = thumbnailIndex === safeIndex;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
      thumbnail.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (updateLightbox) renderLightbox(gallery, safeIndex);
    return true;
  };

  const openLightbox = (gallery) => {
    const lightbox = gallery?.querySelector(lightboxSelector);
    const closeButton = lightbox?.querySelector('[data-lightbox-close]');
    const items = getItems(gallery);
    if (!lightbox || !items.length) return;

    const index = currentIndex(gallery, items.length);
    renderLightbox(gallery, index);
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    lightbox.hidden = false;
    lightbox.removeAttribute('hidden');
    lightbox.setAttribute('aria-hidden', 'false');
    openGallery = gallery;
    closeButton?.focus();
  };

  const closeLightbox = (gallery = openGallery) => {
    const lightbox = gallery?.querySelector(lightboxSelector);
    if (!lightbox) return;

    lightbox.hidden = true;
    lightbox.setAttribute('hidden', '');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = previousBodyOverflow;
    getMainButton(gallery)?.focus();
    if (openGallery === gallery) openGallery = null;
  };

  const move = (gallery, direction) => {
    const items = getItems(gallery);
    if (items.length < 2) return;
    selectImage(gallery, currentIndex(gallery, items.length) + direction, true);
  };

  document.addEventListener('click', (event) => {
    const target = asElement(event.target);
    if (!target) return;

    const thumbnail = target.closest(thumbSelector);
    if (thumbnail) {
      const gallery = thumbnail.closest(gallerySelector);
      if (!gallery) return;
      event.preventDefault();
      const thumbnails = getThumbnails(gallery);
      const index = thumbnails.indexOf(thumbnail);
      if (index >= 0) selectImage(gallery, index, false);
      return;
    }

    const mainButton = target.closest(mainSelector);
    if (mainButton) {
      const gallery = mainButton.closest(gallerySelector);
      if (!gallery) return;
      event.preventDefault();
      openLightbox(gallery);
      return;
    }

    const closeButton = target.closest('[data-lightbox-close]');
    if (closeButton) {
      event.preventDefault();
      closeLightbox(closeButton.closest(gallerySelector));
      return;
    }

    const previousButton = target.closest('[data-lightbox-prev]');
    if (previousButton) {
      event.preventDefault();
      move(previousButton.closest(gallerySelector), -1);
      return;
    }

    const nextButton = target.closest('[data-lightbox-next]');
    if (nextButton) {
      event.preventDefault();
      move(nextButton.closest(gallerySelector), 1);
      return;
    }

    const lightbox = target.closest(lightboxSelector);
    if (lightbox && (target === lightbox || target.hasAttribute('data-lightbox-stage'))) {
      closeLightbox(lightbox.closest(gallerySelector));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!openGallery) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeLightbox(openGallery);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      move(openGallery, -1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      move(openGallery, 1);
    }
  });

  document.addEventListener('touchstart', (event) => {
    const target = asElement(event.target);
    if (!target?.closest(lightboxSelector)) return;
    touchStartX = event.changedTouches[0]?.clientX ?? null;
  }, { passive: true });

  document.addEventListener('touchend', (event) => {
    const target = asElement(event.target);
    const lightbox = target?.closest(lightboxSelector);
    const gallery = lightbox?.closest(gallerySelector);
    if (!gallery || touchStartX === null) return;

    const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
    const distance = touchEndX - touchStartX;
    touchStartX = null;
    if (Math.abs(distance) > 45) move(gallery, distance > 0 ? -1 : 1);
  }, { passive: true });

  const initialise = (root = document) => {
    root.querySelectorAll?.(gallerySelector).forEach((gallery) => {
      const items = getItems(gallery);
      if (items.length) selectImage(gallery, currentIndex(gallery, items.length), false);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(document), { once: true });
  } else {
    initialise(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
